import { X } from 'lucide-react';
import type { BrandFacet, ItemsSearchParams, TagFacet } from '../-types';

interface ActiveFilter {
  key: string;
  value: string;
  type: string;
  id: string | number;
}

interface ActiveFiltersProps {
  currentParams: ItemsSearchParams;
  brands: BrandFacet[];
  tags: TagFacet[];
  onParamsChange: (updates: Partial<ItemsSearchParams>) => void;
  onClearAll: () => void;
}

export function ActiveFilters({
  currentParams,
  brands,
  tags,
  onParamsChange,
  onClearAll,
}: ActiveFiltersProps) {
  // Create lookup maps for brands and tags
  const brandLookup = Object.fromEntries(brands.map(({ id, name }) => [id, name]));
  const tagLookup = Object.fromEntries(
    tags.map((tag) => [tag.valueId, { keyName: tag.keyName, valueName: tag.valueName }]),
  );

  // Build active filters array
  const activeFilters: ActiveFilter[] = [];

  // Add query filter (only if it's a non-empty string)
  if (typeof currentParams.query === 'string' && currentParams.query.length > 0) {
    activeFilters.push({
      key: 'מחרוזת חיפוש',
      value: currentParams.query,
      type: 'query',
      id: 'query',
    });
  }

  // Add brand filters
  if (currentParams.brands && currentParams.brands.length > 0) {
    currentParams.brands.forEach((brandId) => {
      const brandName = brandLookup[brandId] || 'Unknown Brand';
      activeFilters.push({
        key: 'יצרן',
        value: brandName,
        type: 'brand',
        id: brandId,
      });
    });
  }

  // Add tag filters
  if (currentParams.filters && currentParams.filters.length > 0) {
    currentParams.filters.forEach((filterId) => {
      const tag = tagLookup[filterId];
      activeFilters.push({
        key: tag?.keyName ?? 'מסנן',
        value: tag?.valueName ?? `#${filterId}`,
        type: 'filter',
        id: filterId,
      });
    });
  }

  if (currentParams.afterDropOnly) {
    activeFilters.push({
      key: 'ירידת מחיר',
      value: '',
      type: 'afterDropOnly',
      id: 'afterDropOnly',
    });
  }

  if (currentParams.backInStockOnly) {
    activeFilters.push({
      key: 'חזר למלאי',
      value: '',
      type: 'backInStockOnly',
      id: 'backInStockOnly',
    });
  }

  if (currentParams.lowestPricesOnly) {
    activeFilters.push({
      key: 'מחיר נמוך ביותר',
      value: '',
      type: 'lowestPricesOnly',
      id: 'lowestPricesOnly',
    });
  }

  if (currentParams.includeOutOfStock) {
    activeFilters.push({
      key: 'כולל לא במלאי',
      value: '',
      type: 'includeOutOfStock',
      id: 'includeOutOfStock',
    });
  }

  const hasMin =
    typeof currentParams.minPrice === 'number' && !Number.isNaN(currentParams.minPrice);
  const hasMax =
    typeof currentParams.maxPrice === 'number' && !Number.isNaN(currentParams.maxPrice);
  if (hasMin || hasMax) {
    const fmt = (n: number) => new Intl.NumberFormat('he-IL').format(n);
    const minPrice = currentParams.minPrice as number;
    const maxPrice = currentParams.maxPrice as number;
    const label =
      hasMin && hasMax
        ? `₪${fmt(minPrice)} - ₪${fmt(maxPrice)}`
        : hasMin
          ? `מ-₪${fmt(minPrice)}`
          : `עד ₪${fmt(maxPrice)}`;
    activeFilters.push({ key: 'טווח מחירים', value: label, type: 'price', id: 'price' });
  }

  const hasActiveFilters = activeFilters.length > 0;

  // Handle filter removal
  const removeFilter = (filter: ActiveFilter) => {
    switch (filter.type) {
      case 'price':
        onParamsChange({ minPrice: null, maxPrice: null, page: 1 });
        break;
      case 'query':
        onParamsChange({ query: null, page: 1 });
        break;
      case 'brand':
        onParamsChange({
          brands: currentParams.brands?.filter((id) => id !== filter.id),
          brandName: null,
          page: 1,
        });
        break;
      case 'filter':
        onParamsChange({
          filters: currentParams.filters?.filter((id) => id !== filter.id),
          page: 1,
        });
        break;
      case 'afterDropOnly':
        onParamsChange({ afterDropOnly: false, page: 1 });
        break;
      case 'backInStockOnly':
        onParamsChange({ backInStockOnly: false, page: 1 });
        break;
      case 'lowestPricesOnly':
        onParamsChange({ lowestPricesOnly: false, page: 1 });
        break;
      case 'includeOutOfStock':
        onParamsChange({ includeOutOfStock: false, page: 1 });
        break;
    }
  };

  if (!hasActiveFilters) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 py-2">
      <button
        type="button"
        onClick={onClearAll}
        className="group flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-950/40 transition-all duration-200 hover:bg-red-100 dark:hover:bg-red-900/60 hover:border-red-300 dark:hover:border-red-500"
      >
        <X
          className="h-3 w-3 text-red-500 dark:text-red-400 transition-transform duration-200 group-hover:rotate-90"
          strokeWidth={2.5}
        />
        <span className="text-[11px] font-semibold text-red-600 dark:text-red-300">נקה הכל</span>
      </button>

      {activeFilters.map((filter) => (
        <button
          type="button"
          key={`${filter.type}-${filter.id}`}
          onClick={() => removeFilter(filter)}
          className="group flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
        >
          <X
            className="h-3 w-3 text-slate-400 dark:text-slate-500 transition-all duration-200 group-hover:text-red-500 group-hover:rotate-90"
            strokeWidth={2}
          />
          <span className="text-[11px] font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
            {filter.value ? `${filter.key}: ${filter.value}` : filter.key}
          </span>
        </button>
      ))}
    </div>
  );
}
